import Dialog from '@mui/material/Dialog'
import DialogContent from '@mui/material/DialogContent'
import { useTheme } from '@mui/material/styles'
import noop from 'lodash/noop'
import React, { useCallback, useMemo, useRef, useState } from 'react'

import { VisionVideoPlayer } from 'components/common/vision/VisionVideoPlayer'
import { handleError } from 'utils/error'
import { EXCEPTION_MEDIA_KIND, getExceptionMediaSlideKey, getExceptionMediaSlides, toPlayableExceptionVideoUri } from 'utils/exceptionImage'
import { blobToBase64, getExceptionImageBlob } from 'utils/file'

import ExceptionVideoSlide from './exception-card/ExceptionVideoSlide'
import ExceptionGalleryAppBar from './ExceptionGalleryAppBar'
import ExceptionImage from './ExceptionImage'
import SwiperContainer from './SwiperContainer'

import type { ExceptionShape } from './exception-card/types'
import type { SwiperElement } from './SwiperContainer'
import type { Swiper } from 'swiper'

const ExceptionImageUntyped = ExceptionImage as React.ElementType
const ExceptionGalleryAppBarUntyped = ExceptionGalleryAppBar as React.ElementType

const swiperSx = {
  'swiper-slide img': {
    width: '100%',
  },
}

const thumbsSwiperSx = {
  'swiper-slide': {
    backgroundSize: 'cover',
    backgroundPosition: 'center',
  },
  '.thumbs-swiper swiper-slide': {
    width: '25%',
    height: '100%',
  },
  '.thumbs-swiper swiper-slide:not(.swiper-slide-active)': {
    opacity: 0.4,
  },
  '.thumbs-swiper swiper-slide .swiper-slide-active': {
    opacity: 1,
  },
  'swiper-slide img': {
    width: '100%',
    height: '100%',
  },
  '.thumbs-swiper swiper-slide img': {
    objectFit: 'cover',
    objectPosition: 'center',
  },
}

type ExceptionGalleryProps = {
  open: boolean
  onClose?: () => void
  exception: ExceptionShape
  accountId?: string
  workOrderId?: string
}

const ExceptionGallery: React.FC<ExceptionGalleryProps> = ({ open, onClose = noop, exception, accountId, workOrderId }) => {
  const swiperRef = useRef<SwiperElement | null>(null)
  const thumbsSwiperRef = useRef<SwiperElement | null>(null)
  const theme = useTheme()
  const [currentIndex, setCurrentIndex] = useState(0)

  const images = useMemo(() => getExceptionMediaSlides(exception), [exception])
  const hasManyImages = images.length > 1
  // A swiper-slide has no definite height, so a slide asking for 100% collapses to 0.
  const slideHeight = hasManyImages ? 'calc(85vh - 64px)' : 'calc(100vh - 120px)'
  const currentPhoto = images[currentIndex]
  const photoName = currentPhoto?.name ?? ''
  // Only photo slides get a `.png` filename; a video slide is named by its media id.
  const isVideoSlide = currentPhoto?.kind === EXCEPTION_MEDIA_KIND.VIDEO
  const photoFileName = photoName && !isVideoSlide ? `${photoName}.png` : ''
  const createdAt = exception?.createdAt
  const fileLocation = currentPhoto?.original?.fileLocation

  const handleDownloadImage = useCallback(() => {
    getExceptionImageBlob(fileLocation)
      .then(blobToBase64)
      .then((base64: string | undefined) => {
        if (base64) {
          const link = document.createElement('a')
          link.href = base64
          link.download = photoFileName || 'exception.png'
          link.click()
        }
      })
      .catch(handleError)
  }, [fileLocation, photoFileName])

  const handleSlideChange = useCallback((swiperInstance: Swiper) => {
    setCurrentIndex(swiperInstance.realIndex)
  }, [])

  return (
    <Dialog
      fullScreen
      fullWidth
      open={open}
      PaperProps={{ sx: { bgcolor: theme.palette.common.black } }}
      sx={{ zIndex: theme.zIndex.snackbar, color: theme.palette.common.white }}
      onClose={onClose}
    >
      <ExceptionGalleryAppBarUntyped
        createdAt={createdAt}
        photoName={photoFileName}
        showDownloadButton={Boolean(fileLocation)}
        onClose={onClose}
        onDownload={handleDownloadImage}
      />
      <DialogContent className="exceptions-gallery" sx={{ height: '100%', maxHeight: 'calc(100vh - 64px)', pb: 4, overflowY: 'hidden' }}>
        <SwiperContainer
          loop
          navigation
          className="main-swiper"
          spaceBetween={10}
          swiperRef={swiperRef}
          sx={{
            ...swiperSx,
            '.main-swiper': {
              height: hasManyImages ? 'calc(85vh - 56px)' : 'calc(100vh - 56px)',
              width: '100%',
            },
            'swiper-slide': {
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
              width: '100%',
              height: '100%',
            },
            '.main-swiper swiper-slide img': {
              objectFit: 'contain',
              backgroundColor: '#000',
              objectPosition: 'center',
            },
            swiper: {
              height: '100px',
              width: '100%',
            },
            'swiper-container': {
              height: '100%',
              width: '100%',
              '--swiper-navigation-color': '#fff',
            },
          }}
          thumbsSwiperSelector=".thumbs-swiper"
          onSlideChange={handleSlideChange}
        >
          {images.map(image =>
            image.kind === EXCEPTION_MEDIA_KIND.VIDEO ? (
              <swiper-slide key={getExceptionMediaSlideKey(image)}>
                <VisionVideoPlayer
                  showNativeControls
                  showCenterPlayPauseIcon={false}
                  sx={{ height: slideHeight, maxWidth: '1400px', width: '100%' }}
                  variant="embedded"
                  videoUri={toPlayableExceptionVideoUri(image.uri)}
                  workOrderId={workOrderId ?? ''}
                />
              </swiper-slide>
            ) : (
              <ExceptionImageUntyped
                key={getExceptionMediaSlideKey(image)}
                withSwiperSlide
                accountId={accountId}
                exception={exception}
                height={slideHeight}
                ImageSx={{
                  maxWidth: '1400px',
                }}
                photo={image}
                workOrderId={workOrderId}
              />
            )
          )}
        </SwiperContainer>

        <SwiperContainer
          centeredSlides
          freeMode
          watchSlidesProgress
          className="thumbs-swiper"
          slidesPerView={4}
          spaceBetween={10}
          swiperRef={thumbsSwiperRef}
          sx={{
            ...thumbsSwiperSx,
            '.thumbs-swiper': {
              height: hasManyImages ? '15vh' : '0px',
              boxSizing: 'border-box',
              padding: '10px 0',
            },
          }}
        >
          {hasManyImages &&
            images.map(image =>
              image.kind === EXCEPTION_MEDIA_KIND.VIDEO ? (
                <ExceptionVideoSlide key={getExceptionMediaSlideKey(image)} height="100%" thumbnailUri={image.thumbnailUri} />
              ) : (
                <ExceptionImageUntyped
                  key={getExceptionMediaSlideKey(image)}
                  withSwiperSlide
                  accountId={accountId}
                  exception={exception}
                  height={hasManyImages ? '15vh' : '0px'}
                  photo={image}
                  size="small"
                  workOrderId={workOrderId}
                />
              )
            )}
        </SwiperContainer>
      </DialogContent>
    </Dialog>
  )
}

export default ExceptionGallery
